import { MediaUploadError, uploadMedia, type MediaTicket } from '../media/mediaUpload';
import type { ArchiveMedia } from './catalog';

// 9.18.10.4 · D-830 — puts the chosen archive items on the profile, one at a
// time: extract from the ZIP, upload through the normal direct-upload pipeline
// (ticket, bytes, verification), attach to the profile, then delete the
// temporary copy. One at a time keeps memory and storage to a single file and
// makes "stop" mean "after this one".

export type ImportResult = 'added' | 'duplicate' | 'too_large' | 'limit' | 'failed' | 'cancelled';

export type PreparedFile = {
  /** What the native upload reads; on the web an opaque handle. */
  uri: string;
  sizeBytes: number;
  /** Sends the bytes to the signed address; returns the HTTP status. */
  put(uploadUrl: string, contentType: string): Promise<number>;
  /** Removes the temporary copy; called whatever happened. */
  dispose(): void;
};

export type ImportDeps = {
  prepare(item: ArchiveMedia, signal: { readonly aborted: boolean }): Promise<PreparedFile>;
  requestTicket(input: { kind: 'image' | 'video'; contentType: string; sizeBytes: number }): Promise<MediaTicket>;
  finalize(input: { kind: 'image' | 'video'; url: string }): Promise<{ url: string }>;
  attach(input: { url: string; sourceKey: string; sourceTakenAt: string | null }): Promise<void>;
};

export type ImportProgress = { index: number; total: number; item: ArchiveMedia };

/** Reads the server's error code from the API client's error, if any. */
function codeOf(error: unknown): string | null {
  const value = (error as { code?: unknown } | null)?.code;
  return typeof value === 'string' ? value : null;
}

export async function importArchiveMedia(
  items: ArchiveMedia[],
  deps: ImportDeps,
  options: { signal?: { readonly aborted: boolean }; onProgress?: (progress: ImportProgress) => void } = {},
): Promise<Map<string, ImportResult>> {
  const signal = options.signal ?? { aborted: false };
  const results = new Map<string, ImportResult>();
  let stopped: ImportResult | null = null;
  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    if (!stopped && signal.aborted) stopped = 'cancelled';
    if (stopped) { results.set(item.key, stopped); continue; }
    if (item.tooLarge) { results.set(item.key, 'too_large'); continue; }
    options.onProgress?.({ index, total: items.length, item });
    let prepared: PreparedFile | null = null;
    try {
      prepared = await deps.prepare(item, signal);
      const file = prepared;
      const { url } = await uploadMedia({
        requestTicket: (input) => deps.requestTicket({ kind: item.kind, contentType: input.contentType, sizeBytes: input.sizeBytes }),
        putFile: ({ uploadUrl, contentType }) => file.put(uploadUrl, contentType),
        finalize: (input) => deps.finalize({ kind: item.kind, url: input.url }),
      }, { kind: item.kind, uri: file.uri, contentType: item.contentType, sizeBytes: file.sizeBytes });
      await deps.attach({ url, sourceKey: item.key, sourceTakenAt: item.takenAt ? item.takenAt.toISOString() : null });
      results.set(item.key, 'added');
    } catch (error) {
      const code = codeOf(error);
      if (code === 'cancelled' || (signal.aborted && !prepared)) {
        results.set(item.key, 'cancelled');
      } else if (error instanceof MediaUploadError && error.failure.step === 'limit') {
        results.set(item.key, 'too_large');
      } else if (code === 'duplicate') {
        results.set(item.key, 'duplicate');
      } else if (code === 'limit_reached') {
        // The profile is full: nothing after this can be added either.
        results.set(item.key, 'limit');
        stopped = 'limit';
      } else {
        results.set(item.key, 'failed');
      }
    } finally {
      prepared?.dispose();
    }
  }
  return results;
}

export function summarize(results: Map<string, ImportResult>): Record<ImportResult, number> {
  const counts: Record<ImportResult, number> = { added: 0, duplicate: 0, too_large: 0, limit: 0, failed: 0, cancelled: 0 };
  for (const result of results.values()) counts[result] += 1;
  return counts;
}
